'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgChevronLeft from '@/icons/regular/ChevronLeft'
import SvgChevronRight from '@/icons/regular/ChevronRight'
import type { DirectoryPage } from '@/lib/directory/present-releases'
import { tw } from '@/lib/utils'

import { PageSizeSelect } from './page-size-select'

interface ResultsPaginationProps {
  paged: DirectoryPage
  onPageChange: (page: number) => void
  /** When given, a per-page choice sits beside the visible range. */
  pageSizeChoice?: {
    pageSize: number
    options: number[]
    onChange: (pageSize: number) => void
  }
}

type PageToken = number | 'gap'

// Up to seven pages list in full; beyond that the first, the last and the
// current page's neighbours show, with an ellipsis where numbers are skipped.
const pageTokens = (page: number, pageCount: number): PageToken[] => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1)
  }
  const shown = new Set([1, pageCount, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pageCount))
  const tokens: PageToken[] = []
  let previous = 0
  for (const n of [...shown].sort((a, b) => a - b)) {
    if (n - previous > 1) {
      tokens.push('gap')
    }
    tokens.push(n)
    previous = n
  }
  return tokens
}

const buttonClass =
  'focus-visible:outline-lapis inline-flex min-h-[44px] min-w-[40px] items-center justify-center gap-1.5 rounded-xl border px-2.5 text-[14px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 sm:min-w-[44px] sm:px-3'
const idleClass = 'border-navy/25 text-navy hover:border-navy cursor-pointer bg-white'
const disabledClass = 'border-navy/10 text-navy/40 cursor-not-allowed bg-white'

// The page controls under the rows: the visible range (with the per-page
// choice when the caller offers one), then Previous, the page numbers and
// Next. The caller shows it for more than one page or a per-page choice; the
// page numbers themselves hide on a single page.
export const ResultsPagination = ({ paged, onPageChange, pageSizeChoice }: ResultsPaginationProps) => {
  const { t } = useTranslation()
  const { page, pageCount, start, end, total } = paged
  const go = (next: number) => {
    if (next >= 1 && next <= pageCount && next !== page) {
      onPageChange(next)
    }
  }

  return (
    <nav
      aria-label={t('directory.pagination.label', 'Pagination')}
      className="mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-3"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <p aria-live="polite" className="text-navy/70 m-0 text-[14px]">
          {t('directory.pagination.showing', 'Showing {{start}}–{{end}} of {{total}} product families', {
            start,
            end,
            total,
          })}
        </p>
        {pageSizeChoice ? <PageSizeSelect {...pageSizeChoice} /> : null}
      </div>
      {pageCount > 1 ? (
        <ul className="m-0 flex list-none flex-wrap items-center gap-1 p-0 sm:gap-1.5">
          <li>
            <button
              type="button"
              data-tracking="ignore"
              aria-label={t('directory.pagination.previous', 'Previous page')}
              aria-disabled={page === 1 || undefined}
              onClick={() => go(page - 1)}
              className={tw(buttonClass, page === 1 ? disabledClass : idleClass)}
            >
              <SvgChevronLeft aria-hidden className="h-3 w-3 fill-current" />
              <span className="hidden sm:inline">{t('directory.pagination.previousShort', 'Previous')}</span>
            </button>
          </li>
          {pageTokens(page, pageCount).map((token, index) =>
            token === 'gap' ? (
              <li key={`gap-${index}`} aria-hidden className="text-navy/50 px-1 text-[14px]">
                …
              </li>
            ) : (
              <li key={token}>
                <button
                  type="button"
                  data-tracking="ignore"
                  aria-label={t('directory.pagination.page', 'Page {{page}}', { page: token })}
                  aria-current={token === page ? 'page' : undefined}
                  onClick={() => go(token)}
                  className={tw(
                    buttonClass,
                    token === page ? 'border-navy bg-navy cursor-default text-white' : idleClass
                  )}
                >
                  {token}
                </button>
              </li>
            )
          )}
          <li>
            <button
              type="button"
              data-tracking="ignore"
              aria-label={t('directory.pagination.next', 'Next page')}
              aria-disabled={page === pageCount || undefined}
              onClick={() => go(page + 1)}
              className={tw(buttonClass, page === pageCount ? disabledClass : idleClass)}
            >
              <span className="hidden sm:inline">{t('directory.pagination.nextShort', 'Next')}</span>
              <SvgChevronRight aria-hidden className="h-3 w-3 fill-current" />
            </button>
          </li>
        </ul>
      ) : null}
    </nav>
  )
}
